import { atom, useAtom, useSetAtom } from "jotai";
import { useCallback } from "react";
import { atomWithLocalStorage } from "common/storage";

/**
 * One coach mark. `anchor` is matched against a `data-tour-anchor` attribute
 * on the element it points at, rather than a ref threaded down through three
 * components that otherwise have no interest in the tour.
 */
export type HomeTourStep = {
  anchor: "search" | "contribute" | "how-to";
  bodyKey: string;
  /** Where the bubble sits relative to its anchor, before any flip. */
  placement: "above" | "below";
};

/** The frame's order: search first, then contribute, then the way to help. */
export const HOME_TOUR_STEPS: HomeTourStep[] = [
  { anchor: "search", bodyKey: "homeTourSearch", placement: "above" },
  { anchor: "contribute", bodyKey: "homeTourContribute", placement: "below" },
  { anchor: "how-to", bodyKey: "homeTourHowTo", placement: "below" },
];

/**
 * The tour has run in this browser.
 *
 * Persisted and set by finishing *or* dismissing, the same bargain the
 * onboarding flow's completion flag strikes: it asks once and then leaves
 * people alone. Separate from that flag, though — someone who completed
 * onboarding before this tour existed is still owed the tour.
 */
export const homeTourSeenAtom = atomWithLocalStorage<boolean>(
  "desktopHomeTourSeen",
  false,
);

/** Which mark is on screen. In memory: a reload restarts the tour. */
const homeTourStepAtom = atom(0);

/**
 * Bumped by every replay, so analytics can tell a restart from the tour it
 * interrupted — a replay while the tour is still owed changes nothing else
 * that `HomeTour` can see if it was already on the first mark.
 */
export const homeTourRunAtom = atom(0);

export function useHomeTourState() {
  const [seen, setSeen] = useAtom(homeTourSeenAtom);
  const [stepIndex, setStepIndex] = useAtom(homeTourStepAtom);

  const next = useCallback(() => {
    setStepIndex((current) =>
      Math.min(current + 1, HOME_TOUR_STEPS.length - 1),
    );
  }, [setStepIndex]);

  /** Ends the tour, whether from the last step's Done or an Escape. */
  const dismiss = useCallback(() => {
    setSeen(true);
    setStepIndex(0);
  }, [setSeen, setStepIndex]);

  return { isOwed: !seen, stepIndex, next, dismiss };
}

/**
 * Runs the tour again from its first mark — the Learning Hub's "Show me
 * guided tour on the Homepage". Only clears the flag: `HomeTour` picks it up
 * on its own once nothing modal is left on screen.
 */
export function useReplayHomeTour() {
  const setSeen = useSetAtom(homeTourSeenAtom);
  const setStepIndex = useSetAtom(homeTourStepAtom);
  const setRun = useSetAtom(homeTourRunAtom);

  return useCallback(() => {
    setStepIndex(0);
    setSeen(false);
    setRun((run) => run + 1);
  }, [setRun, setSeen, setStepIndex]);
}
